import React from 'react';
import { isStudentPaywalled } from '../lib/subscription/paywall';
import { useStudentCheckout } from '../lib/subscription/checkout';

// Show the pre-expiry nudge once the app-owned trial has this many days or
// fewer remaining. Standalone students have no email, so this in-app banner
// is the only reminder channel (see docs/design-subscription-trial-ownership).
const REMINDER_THRESHOLD_DAYS = 7;

function TrialBanner({ trial, isLinked, subscriptionAction }) {
    // Hooks must run before any early return. Students can subscribe at any
    // point during the trial (forfeiting the remaining days) via this banner.
    const { startCheckout, isLoading, error } = useStudentCheckout('trial_banner');

    // Linked students ride a parent's plan — never show them a trial banner.
    if (isLinked) return null;

    // Once the trial is spent the student hits the dedicated paywall page,
    // which App renders in place of the feed — never as a banner here.
    if (isStudentPaywalled({ trial, isLinked, subscriptionAction })) return null;

    // While the trial is still running, surface a reminder. It gets more
    // prominent in the final week.
    if (!trial || !trial.is_trial) return null;

    // `is_trial` stays true for as long as a trial row exists, even once it
    // lapses (the backend reports the expired trial via `is_expired`). An
    // expired trial means the student is either paywalled (handled above) or
    // already on a paid plan — either way, never nudge them to subscribe.
    if (trial.is_expired) return null;

    const daysLeft = trial.days_remaining;

    if (daysLeft <= REMINDER_THRESHOLD_DAYS) {
        const lead = daysLeft <= 0
            ? 'Last day of your free trial.'
            : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your free trial.`;
        return (
            <div className="bg-amber-50 border border-amber-300 text-amber-800 p-3 mb-4 rounded-lg text-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <span className="font-semibold">{lead}</span>{' '}
                        Subscribe now to keep learning for $3.99/mo.
                    </div>
                    <button
                        type="button"
                        onClick={startCheckout}
                        disabled={isLoading}
                        aria-busy={isLoading}
                        className="shrink-0 bg-primary text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-60"
                    >
                        {isLoading ? 'Starting…' : 'Subscribe now'}
                    </button>
                </div>
                {error && (
                    <div role="alert" className="text-red-600 mt-2">
                        {error}
                    </div>
                )}
            </div>
        );
    }

    const endDate = new Date(trial.trial_end_at).toLocaleDateString();
    return (
        <div className="bg-blue-50 border border-blue-300 text-blue-700 p-3 mb-4 rounded-lg text-sm">
            Your free trial is active — full access until {endDate}.
        </div>
    );
}

export default TrialBanner;
